import type { Hud, HudAction } from '../ui/hud'
import {
  CUBE_SIZES,
  FLOOR_MODES,
  PUSH_MODES,
  SKIN_MODES,
  next,
  type DemoConfig,
} from './config'

/**
 * The config panel: one button per knob, each cycling its own values.
 *
 * The panel is rebuilt whenever the config changes rather than mutating labels
 * one by one — eleven buttons is cheap, and it keeps the labels and the config
 * impossible to desynchronise.
 *
 * Two controls from the research build are deliberately absent. `Filter` cycled
 * the depth filter; it is pinned to `median+hold`, which is the researched
 * default (see `src/depth/filter.ts`). `Floor: level` held the plane normal at
 * world +Y; it measured worse — 51% of cells locked against 77% — and the
 * reasoning is in `solveLevel`. Both remain in the source, so re-exposing
 * either is a one-line change here.
 */

export interface ControlHandlers {
  onDrop(): void
  onClear(): void
  onCalibrate(): void
  onChange(config: DemoConfig): void
}

export class Controls {
  private readonly hud: Hud
  private readonly handlers: ControlHandlers
  private config: DemoConfig

  constructor(hud: Hud, config: DemoConfig, handlers: ControlHandlers) {
    this.hud = hud
    this.handlers = handlers
    this.config = config
    this.render()
  }

  /** Re-renders the panel against a config changed from outside (e.g. Reset). */
  sync(config: DemoConfig): void {
    this.config = config
    this.render()
  }

  private apply(patch: Partial<DemoConfig>): void {
    this.config = { ...this.config, ...patch }
    this.render()
    this.handlers.onChange(this.config)
  }

  private render(): void {
    const { config } = this
    const actions: HudAction[] = [
      { label: 'Drop', onClick: () => this.handlers.onDrop() },
      { label: 'Clear', onClick: () => this.handlers.onClear() },
      {
        label: config.running ? 'Sim: run' : 'Sim: hold',
        onClick: () => this.apply({ running: !config.running }),
      },
      {
        label: `Size: ${((CUBE_SIZES[config.sizeIndex] ?? 0) * 100).toFixed(0)} cm`,
        onClick: () => this.apply({ sizeIndex: (config.sizeIndex + 1) % CUBE_SIZES.length }),
      },
      {
        label: `Push: ${config.push}`,
        onClick: () => this.apply({ push: next(PUSH_MODES, config.push) }),
      },
      {
        label: `Floor: ${config.floor}`,
        onClick: () => this.apply({ floor: next(FLOOR_MODES, config.floor) }),
      },
      {
        label: `Skin: ${config.skin}`,
        onClick: () => this.apply({ skin: next(SKIN_MODES, config.skin) }),
      },
      {
        label: config.stats ? 'Stats: on' : 'Stats: off',
        onClick: () => this.apply({ stats: !config.stats }),
      },
      { label: 'Calibrate', onClick: () => this.handlers.onCalibrate() },
    ]

    this.hud.setActions(actions)
  }
}
